import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';

const CADENCE_OPTIONS = [
  { value: 'daily',      label: 'Daily — once per day' },
  { value: 'daily_ampm', label: 'Twice a day — AM + PM' },
  { value: 'weekday',    label: 'Specific weekdays' },
  { value: 'weekly',     label: 'Once a week' },
  { value: 'monthly',    label: 'Once a month' },
];

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export default function ChoreNew() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = !!id;

  const [users, setUsers] = useState([]);
  const [segments, setSegments] = useState([]);
  const [form, setForm] = useState({
    name: '',
    description: '',
    segment_id: '',
    cadence_type: 'daily',
    weekdays: ['mon', 'wed', 'fri'],
    primary_assignee_id: '',
    backup_assignee_id: '',
    active: true,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    Promise.all([api.get('/api/users'), api.get('/api/segments')])
      .then(([{ users }, { segments }]) => { setUsers(users); setSegments(segments); })
      .catch(e => setErr(e.message));
    if (editing) {
      api.get('/api/chores').then(({ chores }) => {
        const c = chores.find(x => x.id === Number(id));
        if (!c) return;
        setForm({
          name: c.name || '',
          description: c.description || '',
          segment_id: c.segment_id || '',
          cadence_type: c.cadence_type,
          weekdays: c.cadence_detail ? JSON.parse(c.cadence_detail) : ['mon', 'wed', 'fri'],
          primary_assignee_id: c.primary_assignee_id || '',
          backup_assignee_id: c.backup_assignee_id || '',
          active: !!c.active,
        });
      });
    }
  }, [id]);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function toggleDay(d) {
    setForm(f => ({
      ...f,
      weekdays: f.weekdays.includes(d) ? f.weekdays.filter(x => x !== d) : [...f.weekdays, d],
    }));
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const body = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        segment_id: form.segment_id || null,
        cadence_type: form.cadence_type,
        cadence_detail: form.cadence_type === 'weekday' ? form.weekdays : null,
        primary_assignee_id: form.primary_assignee_id ? Number(form.primary_assignee_id) : null,
        backup_assignee_id: form.backup_assignee_id ? Number(form.backup_assignee_id) : null,
        active: form.active,
      };
      if (editing) {
        await api.patch(`/api/chores/${id}`, body);
        navigate('/chores');
      } else {
        await api.post('/api/chores', body);
        navigate('/chores');
      }
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} className="space-y-4 max-w-2xl">
      <h1 className="text-2xl font-serif text-olive-900">{editing ? 'Edit chore' : 'Add chore'}</h1>

      <div className="card grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="label">Name *</label>
          <input className="input" required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Collect eggs" />
        </div>
        <div className="col-span-2">
          <label className="label">Description</label>
          <input className="input" value={form.description} onChange={e => set('description', e.target.value)} placeholder="optional" />
        </div>
        <div>
          <label className="label">Segment</label>
          <select className="input" value={form.segment_id} onChange={e => set('segment_id', e.target.value)}>
            <option value="">—</option>
            {segments.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Cadence</label>
          <select className="input" value={form.cadence_type} onChange={e => set('cadence_type', e.target.value)}>
            {CADENCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        {form.cadence_type === 'weekday' && (
          <div className="col-span-2">
            <label className="label">Days</label>
            <div className="flex gap-1 flex-wrap mt-1">
              {WEEKDAYS.map(d => (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleDay(d)}
                  className={`pill capitalize cursor-pointer ${form.weekdays.includes(d) ? 'bg-olive-700 text-cream-50' : 'bg-cream-100 text-ink-700'}`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
        )}
        <div>
          <label className="label">Primary assignee *</label>
          <select className="input" required value={form.primary_assignee_id} onChange={e => set('primary_assignee_id', e.target.value)}>
            <option value="">—</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Backup</label>
          <select className="input" value={form.backup_assignee_id} onChange={e => set('backup_assignee_id', e.target.value)}>
            <option value="">—</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} />
            Active
          </label>
        </div>
      </div>

      {err && <div className="bg-rust-500/10 text-rust-700 rounded-md px-3 py-2 text-sm">{err}</div>}

      <div className="flex gap-2">
        <button type="button" onClick={() => navigate('/chores')} className="btn-ghost">Cancel</button>
        <button className="btn-primary" disabled={busy}>{busy ? 'Saving…' : editing ? 'Save' : 'Add chore'}</button>
      </div>
    </form>
  );
}
